/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import isNull from 'lodash/isNull';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';

import type {
	ArchiveFormattedMessageForPageQuery as ArchiveFormattedMessageForPageQuery$data,
	ArchiveFormattedMessageForPageQueryVariables as ArchiveFormattedMessageForPageQuery$variables,
} from './__types__/ArchiveFormattedMessageForPageQuery';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TruncatedTitle = styled.span({
	display: 'inline-block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '160px',
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
	verticalAlign: 'bottom',
});

type ArchiveFormattedMessageForPageProps = {
	contentId: string;
	i18nMessage?: MessageDescriptor | null;
	shouldTruncate?: boolean;
	allowNestedPages: boolean;
	useArchivableCount?: boolean;
};

export const ArchiveFormattedMessageForPage: FC<ArchiveFormattedMessageForPageProps> = ({
	contentId,
	i18nMessage,
	shouldTruncate = false,
	allowNestedPages,
	useArchivableCount = false,
}) => {
	const { data, loading, error } = useQuery<
		ArchiveFormattedMessageForPageQuery$data,
		ArchiveFormattedMessageForPageQuery$variables
	>(
		gql`
			query ArchiveFormattedMessageForPageQuery($contentId: ID!, $allowNestedPages: Boolean!) {
				singleContent(id: $contentId) {
					id
					title
					archivableDescendantsCount @include(if: $allowNestedPages)
					visibleDescendantsCount @include(if: $allowNestedPages)
				}
			}
		`,
		{
			variables: { contentId, allowNestedPages },
		},
	);

	if (loading) {
		return null;
	}
	if (error) {
		return <ErrorDisplay error={error} />;
	}

	const title = data?.singleContent?.title;
	const visibleDescendantsCount = data?.singleContent?.visibleDescendantsCount;
	const archivableDescendantsCount = data?.singleContent?.archivableDescendantsCount;

	// return null if title didn't return or nested pages is allowed but
	// visibleDescendantsCount is null, meaning either invalid content or
	// error while calculating count
	if (!title || (allowNestedPages && isNull(visibleDescendantsCount))) {
		return null;
	}

	const formattedTitle = shouldTruncate ? (
		<Tooltip tag="span" position="top" content={title}>
			<TruncatedTitle>{title}</TruncatedTitle>
		</Tooltip>
	) : (
		title
	);

	return i18nMessage ? (
		<FormattedMessage
			{...i18nMessage}
			values={{
				title: formattedTitle,
				count: useArchivableCount ? archivableDescendantsCount : visibleDescendantsCount,
			}}
		/>
	) : (
		<>{formattedTitle}</>
	);
};
